import * as ChannelService from '@channel.io/channel-web-sdk-loader'
import { useQuery } from '@tanstack/react-query'
import { Fragment, useCallback, useContext, useEffect, useRef, useState } from 'react'
import type { UIEvent } from 'react'
import { useLocation } from 'react-router'
import { Pagination } from 'swiper'
import { Swiper, SwiperSlide } from 'swiper/react'
import { isAxiosError } from 'axios'
import dayjs from 'dayjs'
import styled from 'styled-components'

import { PRODUCT_TAB, QUERY_KEY } from '../../constants'
import type { PRODUCT_TAB_TYPE } from '../../constants'
import type { components } from '../../constants/schema'

import { calculateAge, downloadAttachment, clipboardCopy } from '../../utils'
import { abrViewSubscriptionProductDetails } from '../../utils/abr'
import { requestIdentify, convertIdentityVerificationResult } from '../../utils/identityVerification'
import { queryClient } from '../../utils/queryClient'

import { StoresContext } from '../../provider/StoreProvider'
import { ModalContext } from '../../provider/ModalProvider'

import { useAlert, useScroll } from '../../hooks'
import { useNavigate } from '../../hooks/useNavigate'
import useParamId from '../../hooks/useParamId'
import { useUserInfo } from '../../hooks/useUserInfo'

import { getProductDetail } from '../../api/product'

import { Wrap } from '../../components/container/Wrap'
import { Content } from '../../components/container/Content'
import { RowBetween } from '../../components/common'
import { Button } from '../../components/button'
import { Text } from '../../components/text'
import { ProductInfoCard } from '../../components/card/ProductInfoCard'
import { DivideLine } from '../../components/divide'
import { DetailBanner } from '../../components/banner'
import { Header } from '../../components/header'
import { FOOTER_MIN_HEIGHT } from '../../components/footer'
import { Tab } from '../../components/tab'
import { ArtipioContents, ArtipioContentItem } from '../../components/contents'
import { Spinner } from '../../components/spinner/Spinner'
import { AlreadyVerificationModal } from '../settings/modal/AlreadyVerificationModal'
import type { IdentifyInfo } from '../settings/modal/AlreadyVerificationModal'
import DetailFooter from './components/DetailFooter'
import Precautions from './components/Precautions'

import { Color } from '../../theme/color'

import { ReactComponent as HomeWhiteIcon } from '../../assets/ico/detail/home_white.svg'
import { ReactComponent as HomeBlackIcon } from '../../assets/ico/detail/home_black.svg'
import { ReactComponent as ShareWhiteIcon } from '../../assets/ico/detail/share_white.svg'
import { ReactComponent as ShareBlackIcon } from '../../assets/ico/detail/share_black.svg'
import { ReactComponent as StateUpIcon } from '../../assets/ico/common/expand/expand_up.svg'
import { ReactComponent as DownloadIcon } from '../../assets/ico/common/download_18.svg'

const ALLOTMENT_WHITE_LIST = [
  'SALE_ALLOTMENT_IN_PROGRESS',
  'SALE_COMPLETED',
  'SELL',
  'FINISH',
  'FULLY_COMPLETED',
  'DESTRUCTED',
]

function RowContents({ title, content }: { title: string; content: string }) {
  return (
    <RowBetween>
      <Text size={16} color="text_secondary">
        {title}
      </Text>
      <Text size={16} weight={500}>
        {content}
      </Text>
    </RowBetween>
  )
}

function ProductInfoButton({ content, onClick }: { content: string; onClick?: () => void }) {
  return (
    <Button type="dark" size="compact" block={true} onClick={onClick}>
      {content}
      <DownloadIcon />
    </Button>
  )
}

export function ProductDetail() {
  const {
    commonStore: { setShowTab, setInvalidAccess, setIsLoading },
  } = useContext(StoresContext)
  const { openModal } = useContext(ModalContext)

  // const [productDetail, setProductDetail] = useState<components['schemas']['ProductDetailDto'] | null>(null)
  const [tabType, setTabType] = useState<PRODUCT_TAB_TYPE>(PRODUCT_TAB.INVESTMENT)
  const [isDropdownProductInfo, setIsDropdownProductInfo] = useState<boolean>(false)
  const [tabOffsetHeight, setTabOffsetHeight] = useState<number | undefined>()
  const [tabItemHeight, setTabItemHeight] = useState<{ [key in PRODUCT_TAB_TYPE]: number }>({
    INVESTMENT: 0,
    WORK: 0,
  })
  const [hasTabItemScroll, setHasTabItemScroll] = useState<boolean>(false)

  const navigate = useNavigate()
  const location = useLocation()
  const paramId = useParamId()
  const [isScrollHeader, onScrollHeader] = useScroll({
    breakPoint: (window.innerWidth > 500 ? 500 : window.innerWidth) * 0.9 - 46,
  })
  const [isScrollTab, onScrollTab] = useScroll({ breakPoint: (tabOffsetHeight || 0) - 46 })
  const openAlert = useAlert()

  const { data: userInfo } = useUserInfo()
  const {
    data: productDetail,
    isPending,
    isError,
    error,
  } = useQuery({
    queryKey: [QUERY_KEY.product, paramId],
    queryFn: () => getProductDetail(paramId as number),
    enabled: typeof paramId === 'number',
  })

  const contentRef = useRef<HTMLDivElement>(null)

  const contentWrapRef = useCallback(
    (node: HTMLDivElement) => {
      if (node) setTabOffsetHeight(node.offsetTop)
    },
    [tabType, isDropdownProductInfo],
  )
  const investInfoRef = useCallback(
    (node: HTMLDivElement) => {
      if (node && tabType === 'INVESTMENT')
        setTabItemHeight((prevState) => ({ ...prevState, INVESTMENT: node.offsetTop - 86 }))
    },
    [tabType, isDropdownProductInfo],
  )
  const workInfoRef = useCallback(
    (node: HTMLDivElement) => {
      if (node && tabType === 'WORK') setTabItemHeight((prevState) => ({ ...prevState, WORK: node.offsetTop - 86 }))
    },
    [tabType, isDropdownProductInfo],
  )

  useEffect(() => {
    setShowTab(false)
  }, [])

  useEffect(() => {
    if (error && isAxiosError(error)) {
      console.log({ error })
      if (error.response?.data.CODE === 'API_000404') {
        setInvalidAccess(true)
      }
    }
  }, [error])

  useEffect(() => {
    abrViewSubscriptionProductDetails(productDetail ?? {})
    ChannelService.track('view_subscription_product_details')
  }, [productDetail])

  // TODO:본인인증 후 헤더에서 뒤로가기 누르면 이상하게 뒤로 넘어감
  useEffect(() => {
    const isVerification = !!userInfo && localStorage.getItem('isVerification') === 'true'
    if (!isVerification) return

    const identify = async (data: components['schemas']['ReqQueryIdentication']) => {
      if (!isVerification) return

      const { birthday } = userInfo
      const response = await requestIdentify(data)

      await queryClient.invalidateQueries({ queryKey: [QUERY_KEY.userInfo] })

      if (isAxiosError(response) && response.response?.data.CODE === 'IDNTY_050005') {
        const info: IdentifyInfo = response.response?.data?.info
        if (!info) return
        openModal({ id: 'ALREADY_VERIFICATION', Component: () => <AlreadyVerificationModal info={info} /> })
        return
      }

      if (typeof birthday === 'undefined' || calculateAge(birthday) < 19) {
        openAlert({
          content: (
            <Text size={16} weight={500} lineHeight={24} $textAlign="center">
              만19세 이상부터 구매가 가능해요.
              <br />
              자세한 내용은 FAQ를 참고해주세요.
              <br />
            </Text>
          ),
        })
        return
      }
    }

    if (location.search.includes('identityVerificationId')) {
      const convertQuery = convertIdentityVerificationResult(location.search.substring(1))
      identify(convertQuery)
    }
  }, [location, userInfo])

  useEffect(() => {
    if (!hasTabItemScroll) return
    const scroll = () => {
      contentRef.current?.scroll({ top: tabItemHeight[tabType] + 1, behavior: 'smooth' })
      setHasTabItemScroll(false)
    }
    scroll()
  }, [tabItemHeight, hasTabItemScroll, tabType])

  if (isPending || isError) {
    return (
      <Wrap>
        <Header type="SUB" fixed={true} href={location.search ? '/market' : undefined} />
        <SpinnerContainer>
          <Spinner />
        </SpinnerContainer>
      </Wrap>
    )
  }

  const handleProductInfoDropdown = () => setIsDropdownProductInfo((prevState) => !prevState)

  const tabScrollHandler = (e: UIEvent<HTMLDivElement>) => {
    onScrollHeader(e)
    onScrollTab(e)
    const scrollTop = e.currentTarget.scrollTop
    if (tabType !== 'INVESTMENT' && !hasTabItemScroll && scrollTop < tabItemHeight['WORK']) {
      setTabType('INVESTMENT')
    } else if (
      tabType !== 'WORK' &&
      !hasTabItemScroll &&
      scrollTop > tabItemHeight['INVESTMENT'] &&
      scrollTop > tabItemHeight['WORK']
    ) {
      setTabType('WORK')
    }
  }

  const goHome = () => navigate('/home')

  const copyUrl = () =>
    clipboardCopy(window.location.href, { message: '작품 페이지 주소가 복사되었어요.', isFooter: true })

  return (
    <Wrap>
      <Header
        type="SUB"
        fixed={true}
        isScrollEvent={true}
        isScrolling={isScrollHeader}
        href={location.search ? '/market' : undefined}
        rightItem={
          <HeaderRightItem $gap={16}>
            {isScrollHeader ? (
              <>
                <ShareBlackIcon onClick={copyUrl} />
                <HomeBlackIcon onClick={goHome} />
              </>
            ) : (
              <>
                <ShareWhiteIcon onClick={copyUrl} />
                <HomeWhiteIcon onClick={goHome} />
              </>
            )}
          </HeaderRightItem>
        }
      />
      <StyledContent ref={contentRef} backgroundColor="gray_1" isPullEvent={true} onScroll={tabScrollHandler}>
        {/* 배너 */}
        <DetailBanner>
          <Swiper pagination={true} modules={[Pagination]} className="mySwiper">
            {productDetail.images?.map(({ id, filePath }) => (
              <SwiperSlide key={id}>
                <img
                  src={filePath}
                  onError={({ currentTarget }) => {
                    currentTarget.onerror = null
                    currentTarget.src = '/img/thumbnail.svg'
                  }}
                />
              </SwiperSlide>
            ))}
          </Swiper>
        </DetailBanner>
        <ProductInfoContainer>
          {/* 작품 정보 */}
          <ProductInfoCard>
            <ProductInfo>
              <Text size={24} weight={600} block={true}>
                {productDetail.artist}
              </Text>
              <Text size={14} block={true} color="text_secondary">
                {productDetail.name}
              </Text>
            </ProductInfo>
            <DivideLine />
            <BetweenWrap>
              <Text size={16} color="text_secondary">
                청약 기간
              </Text>
              <Text size={16} weight={500} lineHeight={24}>
                {dayjs(productDetail.activeProductSale?.releaseDate).subtract(9, 'hour').format('YYYY.MM.DD HH:mm')}
                부터
                <br />
                {dayjs(productDetail.activeProductSale?.untilDate).subtract(9, 'hour').format('YYYY.MM.DD HH:mm')}
                까지
              </Text>
            </BetweenWrap>
            <RowBetween>
              <Text size={16} color="text_secondary">
                총 발행 금액
              </Text>
              <Text size={16} weight={500}>
                {productDetail.activeProductSale?.totalIssuedPrice?.toLocaleString()}원
              </Text>
            </RowBetween>
          </ProductInfoCard>
          {ALLOTMENT_WHITE_LIST.includes(productDetail.overallStatus as string) ? (
            <>
              {/* 배정 정보 */}
              <ProductInfoCard>
                <RowBetween>
                  <Text size={18} weight={600}>
                    배정 정보
                  </Text>
                  <StateUpIcon
                    onClick={handleProductInfoDropdown}
                    style={{ transform: `rotateZ(${isDropdownProductInfo ? '180' : '0'}deg)` }}
                  />
                </RowBetween>
                {!isDropdownProductInfo && (
                  <>
                    <RowBetween>
                      <Text size={16} color="text_secondary">
                        배정 방식
                      </Text>
                      <Text size={16} weight={500} lineHeight={24} $textAlign="right">
                        {productDetail.activeProductSale?.allotmentMethodText}
                      </Text>
                    </RowBetween>
                    <RowContents
                      title="총 배정 증권 수"
                      content={`${productDetail.activeProductSale?.totalIssuedShares?.toLocaleString()}주`}
                    />
                    <RowContents
                      title="사업자 배정 수"
                      content={`${productDetail.activeProductSale?.preAllottedShares?.toLocaleString()}주`}
                    />
                    <RowContents
                      title="일반 배정 수"
                      content={`${(
                        (productDetail.activeProductSale?.totalIssuedShares ?? 0) -
                        (productDetail.activeProductSale?.preAllottedShares ?? 0)
                      ).toLocaleString()}주`}
                    />
                    <RowContents
                      title="주당 공모가 액"
                      content={`${productDetail.activeProductSale?.offerPrice?.toLocaleString()}원`}
                    />
                    <RowContents
                      title="배정 확정일"
                      content={dayjs(productDetail.activeProductSale?.allotmentDate)
                        .subtract(9, 'hour')
                        .format('YYYY.MM.DD')}
                    />
                    <DivideLine />
                  </>
                )}
                <ProductInfoButtons>
                  {productDetail.activeProductSale?.files?.map(
                    ({ fileItem, fileItemId, productSaleFileItemType, productSaleFileItemLabel }) => {
                      if (typeof fileItem === 'undefined' || productSaleFileItemType === 'DOCS_INVESTOR_LIST') {
                        return <Fragment key={`f${fileItemId}`} />
                      }

                      const download = async () => {
                        if (!fileItem.filePath || !fileItem.filename) return
                        await downloadAttachment(fileItem.filePath, fileItem.filename)
                      }

                      return (
                        <ProductInfoButton
                          key={fileItemId}
                          content={productSaleFileItemLabel ?? ''}
                          onClick={download}
                        />
                      )
                    },
                  )}
                </ProductInfoButtons>
              </ProductInfoCard>
            </>
          ) : (
            <>
              {/* 청약 정보 */}
              <ProductInfoCard>
                <RowBetween>
                  <Text size={18} weight={600}>
                    청약 정보
                  </Text>
                  <StateUpIcon
                    onClick={handleProductInfoDropdown}
                    style={{ transform: `rotateZ(${isDropdownProductInfo ? '180' : '0'}deg)`, cursor: 'pointer' }}
                  />
                </RowBetween>
                {!isDropdownProductInfo && (
                  <>
                    <RowBetween>
                      <Text size={16} color="text_secondary">
                        배정 방식
                      </Text>
                      <Text size={16} weight={500} lineHeight={24} $textAlign="right">
                        {productDetail.activeProductSale?.allotmentMethodText}
                      </Text>
                    </RowBetween>
                    <RowContents
                      title="모집 증권 수"
                      content={`${productDetail.activeProductSale?.totalIssuedShares?.toLocaleString()}주`}
                    />
                    <RowContents
                      title="사업자 선 배정"
                      content={`${productDetail.activeProductSale?.preAllottedShares?.toLocaleString()}주`}
                    />
                    <RowContents
                      title="일반 청약 배정"
                      content={`${(
                        (productDetail.activeProductSale?.totalIssuedShares ?? 0) -
                        (productDetail.activeProductSale?.preAllottedShares ?? 0)
                      ).toLocaleString()}주`}
                    />
                    <RowContents
                      title="주당 공모가 액"
                      content={`${productDetail.activeProductSale?.offerPrice?.toLocaleString()}원`}
                    />
                    <RowContents
                      title="배정 확정일"
                      content={dayjs(productDetail.activeProductSale?.allotmentDate)
                        .subtract(9, 'hour')
                        .format('YYYY.MM.DD')}
                    />
                    <DivideLine />
                  </>
                )}
                <ProductInfoButtons>
                  {productDetail.activeProductSale?.files?.map(
                    ({ fileItem, fileItemId, productSaleFileItemType, productSaleFileItemLabel }) => {
                      if (typeof fileItem === 'undefined' || productSaleFileItemType === 'DOCS_INVESTOR_LIST') {
                        return <Fragment key={`f${fileItemId}`} />
                      }

                      const download = async () => {
                        if (!fileItem.filePath || !fileItem.filename) return

                        setIsLoading(true)

                        await downloadAttachment(fileItem.filePath, fileItem.filename)

                        setTimeout(() => {
                          setIsLoading(false)
                        }, 500)
                      }

                      return (
                        <ProductInfoButton
                          key={fileItemId}
                          content={productSaleFileItemLabel ?? ''}
                          onClick={download}
                        />
                      )
                    },
                  )}
                </ProductInfoButtons>
              </ProductInfoCard>
            </>
          )}
        </ProductInfoContainer>
        <ProductContentWrap ref={contentWrapRef}>
          <Tab<PRODUCT_TAB_TYPE>
            {...(tabOffsetHeight ? { isScrolling: isScrollTab } : undefined)}
            activeIndex={Object.keys(PRODUCT_TAB).findIndex((v) => v === tabType)}
            items={[
              { text: '투자 정보', value: PRODUCT_TAB.INVESTMENT },
              { text: '작품 정보', value: PRODUCT_TAB.WORK },
            ]}
            onChange={(value) => {
              if (hasTabItemScroll) return
              setTabType(value)
              setHasTabItemScroll(true)
            }}
          />
          {/* 투자 정보 */}
          <ArtipioContents ref={investInfoRef}>
            {productDetail.activeProductSale?.contents?.map(({ type, content, prop }, index) => {
              const isFirstImg = type === 'image' && index === 0
              const isLastImg =
                type === 'image' && index === Number(productDetail.activeProductSale?.contents?.length) - 1
              return (
                <ArtipioContentItem
                  key={index}
                  type={type}
                  content={content}
                  prop={prop}
                  isFirstImg={isFirstImg}
                  isLastImg={isLastImg}
                />
              )
            })}
          </ArtipioContents>
          {/* 작품 정보 */}
          <ArtipioContents ref={workInfoRef}>
            {productDetail.contents?.map(({ type, content, prop }, index) => {
              const isFirstImg = type === 'image' && index === 0
              const isLastImg = type === 'image' && index === Number(productDetail.contents?.length) - 1
              return (
                <ArtipioContentItem
                  key={index}
                  type={type}
                  content={content}
                  prop={prop}
                  isFirstImg={isFirstImg}
                  isLastImg={isLastImg}
                />
              )
            })}
          </ArtipioContents>
        </ProductContentWrap>
        {/* 청약 유의 사항 */}
        <Precautions />
      </StyledContent>
      {productDetail.subscriptionStatus !== 'SUBSCRIPTION_CLOSED' && (
        <DetailFooter
          activeProductSale={productDetail.activeProductSale}
          overallStatus={productDetail.overallStatus}
          subscriptionStatus={productDetail.subscriptionStatus}
          subscriptionStatusLabel={productDetail.subscriptionStatusLabel}
        />
      )}
    </Wrap>
  )
}

const HeaderRightItem = styled(RowBetween)`
  > * {
    cursor: pointer;
  }
`

const StyledContent = styled(Content)`
  padding-bottom: calc(${FOOTER_MIN_HEIGHT}px + 33px);
`

const SpinnerContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
`

const ProductInfoContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px;
  ${RowBetween} {
    gap: 16px;
    * {
      &:first-child {
        flex-shrink: 0;
      }
      &:last-child {
        word-break: keep-all;
      }
    }
  }
  @media (max-width: 340px) {
    padding: 16px 0;
  }
`

const ProductInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const BetweenWrap = styled.div`
  display: flex;
  justify-content: space-between;
`

const ProductInfoButtons = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
`

const ProductContentWrap = styled.div`
  background-color: ${Color.white};
`
